import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Spell, 
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";

import ViewSpell from "@dndComponents/display/ViewSpell";
import FilterBox from "@dndComponents/filtering/FilterBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import LetterLinks from "@components/Navigation/LetterLinks";
import PageLinks from "@components/Navigation/PageLinks";
import ButtonBox from "@components/input/buttons/ButtonBox";

interface Props {
  spell_ids: string[];
  onChange?: (changed: string[], mapping: { [old_id: string]: string }) => void; 
  onChangeByLevel?: (changed: { [level: number]: string[] }, mapping: { [new_id: string]: string }) => void;
  disabled: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SelectSpellIndex({ 
  spell_ids,
  onChange,
  onChangeByLevel,
  disabled
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [filtered, setFiltered] = useState<Spell[]>([]);
  const [page_num, setPageNum] = useState(0);
  const [start_letter, setStartLetter] = useState("");
  const [expanded, setExpanded] = useState("");
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getObjects({data_type: "spell"});
    setSpells(res as Spell[]);
    setFiltered(res as Spell[]);
    setLoading(false);
    setFetched(true);
  }, []);

  useEffect(() => {
    if (!fetched) {
      load()
    }
  }, [fetched, load]);

  const updateFilter = (changed: any) => {
    const the_filter = {...changed};
    // console.log(changed);
    if (the_filter.effect_type) {
      the_filter.effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    const use_list = the_filter.on_list === undefined ? spells : (the_filter.on_list ? spells.filter(o => common_util.anyOverlaps(spell_ids, o.variant_ids)) : spells.filter(o => !common_util.anyOverlaps(spell_ids, o.variant_ids)));
    delete the_filter.on_list;
    // setTrueFilter(the_filter);
    const new_filtered = api.filter("spell", use_list, the_filter);
    setFiltered(new_filtered);
    // setReloading(true);
    setPageNum(0);
    setStartLetter("");
  }

  const { classes, cx } = useStyles();

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const page_size = 7;
    let use_list = [...filtered];
    // console.log(use_list);
    if (start_letter !== "") {
      use_list = use_list.filter(o => o.name.startsWith(start_letter));
    }

    console.log(spell_ids);
    const missing_spells = spells.filter(o => spell_ids.includes(o._id)).length !== spell_ids.length;
    use_list = use_list.sort((a, b) => {
      if (a.level === b.level) {
        return a.name.localeCompare(b.name);
      }
      return a.level - b.level;
    });
    const page_count = Math.ceil(use_list.length / page_size);
    const filtered_and_paged = use_list.slice(page_size * page_num, page_size * (page_num + 1));
    return (
      <Grid container spacing={0} direction="row">
        { missing_spells && 
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              name="Update Older Spells"
              disabled={disabled}
              onClick={() => {
                const mapping: { [new_id: string]: string } = {};
                let new_list = [...spell_ids];
                const all_spell_ids = spells.map(o => o._id);
                const missing_spell_ids = spell_ids.filter(o => !all_spell_ids.includes(o));
                for (const id of missing_spell_ids) {
                  const new_spell = spells.find(o => o.variant_ids.includes(id) && o.isDominant());
                  if (new_spell) {
                    new_list.push(new_spell._id);
                    new_list = new_list.filter(o2 => o2 !== id);
                    mapping[new_spell._id] = id;
                  }
                }
                if (onChange) {
                  onChange(new_list, mapping);
                } else if (onChangeByLevel) {
                  const spells_by_level: { [level: number]: string[] } = {};
                  for (const id of new_list) {
                    const spell = spells.find(o => o._id === id);
                    if (spell) {
                      if (spells_by_level[spell.level] === undefined) {
                        spells_by_level[spell.level] = [];
                      }
                      spells_by_level[spell.level].push(spell._id);
                    }
                  }
                  onChangeByLevel(spells_by_level, mapping);
                }
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <FilterBox 
            data_type="spell"
            dont_use_api
            the_filter={{ search_string: [""] }}
            extra_fields={["on_list"]}
            quick_fields={["school","spell_level","on_list"]}
            all_objects={spells}
            onChange={updateFilter}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
        { filtered_and_paged.map(obj => {
          return (
            <Grid
              key={obj._id}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx(classes.root)}>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <CheckBox 
                    disabled={disabled}
                    value={ spell_ids.includes(obj._id)}
                    onChange={(changed: boolean) => {
                      let new_list = [...spell_ids];
                      if (changed) {
                        new_list.push(obj._id);
                      } else {
                        new_list = new_list.filter(o2 => o2 !== obj._id);
                      }
                      if (onChange) {
                        onChange(new_list, {});
                      } else if (onChangeByLevel) {
                        const spells_by_level: { [level: number]: string[] } = {};
                        for (const id of new_list) {
                          const spell = spells.find(o => o._id === id);
                          if (spell) {
                            if (spells_by_level[spell.level] === undefined) {
                              spells_by_level[spell.level] = [];
                            }
                            spells_by_level[spell.level].push(spell._id);
                          }
                        }
                        onChangeByLevel(spells_by_level, {});
                      }
                    }}
                  />
                </Grid>
                <Grid
                  className="clickable"
                  onClick={() => {
                    setExpanded(expanded === obj._id ? "" : obj._id);
                  }}
                  size={{
                    xs: 10
                  }}>
                  <ViewSpell spell={obj} />
                </Grid>
                { expanded === obj._id &&
                  <Grid
                    style={{ border: "1px solid", padding: "4px", backgroundColor: "black" }}
                    size={{
                      xs: 12
                    }}>
                    <DisplayStringWithReplacements display_string={obj.description} />
                  </Grid>
                }
              </Grid>
            </Grid>
          );
        })}
        <Grid
          size={{
            xs: 12
          }}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <LetterLinks onChange={setStartLetter} />
        </Grid>
      </Grid>
    ); 
  }
}
